"use client";
import { Fragment, useEffect, useState } from "react";
import PricingCard, { TPricingCard } from "./PricingCard";
import * as S from "./styles.ts";
import { H1, Image, P, StyledLink } from "@template/components";
import UserCount from "@template/blocks/PricingSection/component/UserCount";
import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import useCurrency from "@/lib/helpers/hooks/useCurrency.ts";
import { getPlanPrices } from "@/redux/slices/store/thunks/getPlanPrices.ts";
import MostPopular from "@/lib/images/Mostpopular.svg";
import { TBillingInterval } from "@/redux/slices/user/types.ts";
import Link from "next/link";
import UniversalMarkdown from "@template/blocks/universal-markdown.tsx";

type TMarkdownValue = string | any[];

interface IPlanFeature {
  iconName?: string;
  label?: TMarkdownValue;
  tooltip?: string;
}

interface IPlanProps {
  copy?: TMarkdownValue;
  features?: {
    content?: string;
  }[];
  list?: {
    ukFeatures?: IPlanFeature[];
    usdFeatures?: IPlanFeature[];
  };
  featureFooter?: {
    content?: string;
    span?: string;
    link?: {
      label?: string;
      href?: string;
      title?: string;
    };
  };
}
interface TPricingSectionComponentProps {
  heading?: string;
  footer?: string;
  countOptions: {
    defaultValue: number;
    showInput?: boolean;
    showSlider?: boolean;
  };
  default?: boolean;
  hideStaticFooter?: boolean;
  corePlan?: IPlanProps;
  standardPlan?: IPlanProps;
  premiumPlan?: IPlanProps;
}

const slateToText = (value?: TMarkdownValue): string => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    return value
      .map((node: any) =>
        (node?.children || []).map((child: any) => child?.text || "").join("")
      )
      .join("\n")
      .trim();
  }
  return "";
};

const isMarkdownEmpty = (value?: TMarkdownValue) => slateToText(value) === "";

const usePricingCards = () => {
  const currency = useCurrency();
  const plans = useTypedSelector((state) => state.store.plan_prices.plans);

  const hasAddons = plans[0]?.available_addons?.length > 0;
  const monthlyCost = hasAddons
    ? (plans[0].available_addons[0]?.costs?.monthly / 100).toFixed(2)
    : "1.99";

  const priceDisplay = `${currency}${monthlyCost}`;
  const cards: Partial<TPricingCard>[] = [
    {
      footer: (
        <Link
          href="/voice"
          title="Compare plan features"
        >
          + many <span>More Features</span>
        </Link>
      ),
      pills: [
        {
          children: "Call Routing",
        },
        {
          children: "Desktop & Mobile Apps",
        },
      ],

      copy: "Perfect for startups and those looking for virtual landline numbers with a Cloud service",
      features: ["100 Minutes Included"],
      list: [
        {
          label: "Call Routing",
        },
        {
          label: "Desktop & Mobile Apps",
        },
        {
          label: "Unlimited Inbound Calls",
        },
        {
          label: "Call Routing & Forwarding",
        },
        {
          label: "Time of Day Call Handling",
        },
        {
          label: "Unlimited Voicemail",
        },
        {
          label: "Business Messaging",
        },
        {
          label: "30 Day Call Recording",
          tooltip: `Options to extend at checkout. Only ${priceDisplay}/per user per month.`,
        },
      ],
    },
    {
      footer: (
        <Link
          href="/voice"
          title="Compare plan features"
        >
          + everything from <span>Core</span>
        </Link>
      ),
      pills: [
        {
          children: "IVR Call Menus",
        },
        {
          children: "Call Monitoring",
        },
      ],

      copy: "Our most popular plan. Ideal for small offices, includes IVR call menus and more",
      features: ["750 Minutes Included"],
      list: [
        {
          label: "Multi-Level IVR Call Menus",
        },
        {
          label: "Voicemail Transcription",
        },
        {
          label: "Call Monitoring",
        },
        {
          label: "CRM & App Integrations",
        },
        {
          label: "Phone Book Manager",
        },
        {
          label: "Click To Dial",
        },
        {
          label: "Caller ID Routing",
        },
        {
          label: "30 Day Call Recording",
          tooltip: `Options to extend at checkout. Only ${priceDisplay}/per user per month.`,
        },
      ],
    },
    {
      footer: (
        <Link
          href="/voice"
          title="Compare plan features"
        >
          + Everything from <span>Core & Standard</span>
        </Link>
      ),
      copy: "Best for those needing unlimited minutes, international calls, and enterprise features",
      pills: [
        {
          children: "Call Queueing",
        },
        {
          children: "Wallboard & Reporting",
        },
      ],

      features: [`Unlimited Minutes`],
      list: [
        {
          iconName: "AILogo",
          label: "AI Call Transcription",
        },
        {
          iconName: "AILogo",
          label: "AI Call Summaries",
        },
        {
          label: "12 Months Call Recording",
        },
        {
          label: "Smart Call Queuing",
        },
        {
          label: "Call Analytics & Reporting",
        },
        {
          label: "Text to Speech",
        },
        {
          label: "Call Quality Feedback",
        },
        {
          label: "Cloud Storage Sync",
        },
      ],
    },
  ];

  return cards;
};

const PricingSectionComponent = ({
  heading,
  footer,
  countOptions = {
    defaultValue: 1,
    showInput: true,
    showSlider: true,
  },
  default: useDefault = false,
  hideStaticFooter = false,
  corePlan,
  standardPlan,
  premiumPlan,
}: TPricingSectionComponentProps) => {
  const user = useTypedSelector((state) => state.user.user);

  const dispatch = useTypedDispatch();
  const defaultCards = usePricingCards();
  const currency = useCurrency();

  const mergePlan = (
    card: Partial<TPricingCard>,
    cms?: IPlanProps
  ): Partial<TPricingCard> => {
    if (useDefault || !cms) return card;

    const cmsMainFeatures = (cms.features || [])
      .map((feature) => feature?.content?.trim())
      .filter(Boolean) as string[];

    const cmsFeatures =
      currency === "$" ? cms.list?.usdFeatures : cms.list?.ukFeatures;
    const validFeatures = (cmsFeatures || []).filter(
      (feature) => !isMarkdownEmpty(feature?.label)
    );

    const ff = cms.featureFooter;
    const footerHref = ff?.link?.href;
    const footerText = ff?.content || ff?.link?.label;
    const hasCmsFooter = !!footerHref && (!!footerText || !!ff?.span);

    return {
      ...card,
      copy: !isMarkdownEmpty(cms.copy) ? slateToText(cms.copy) : card.copy,
      features: cmsMainFeatures.length ? cmsMainFeatures : card.features,
      list: validFeatures.length
        ? (validFeatures.map((feature) => ({
            iconName: feature.iconName,
            label: (
              <UniversalMarkdown>{feature.label}</UniversalMarkdown>
            ) as any,
            tooltip: slateToText(feature.tooltip) || undefined,
          })) as any)
        : card.list,
      footer: hasCmsFooter ? (
        <Link
          href={footerHref as string}
          title={ff?.link?.title || footerText || ""}
        >
          {footerText} {ff?.span && <span>{ff.span}</span>}
        </Link>
      ) : (
        card.footer
      ),
    };
  };

  const cmsPlans = [corePlan, standardPlan, premiumPlan];
  const pricingCards = defaultCards.map((card, index) =>
    mergePlan(card, cmsPlans[index])
  );

  const plans = useTypedSelector((state) => state.store.plan_prices.plans);
  const plansLoaded = useTypedSelector(
    (state) => state.store.plan_prices.loaded
  );

  const [userCount, setUserCount] = useState(countOptions.defaultValue);
  const [renewalPeriod, setRenewalPeriod] =
    useState<TBillingInterval>("yearly");

  const usedPlans = plans.map((plan, index) => ({
    ...pricingCards[index],
    type: plan.id - 1,
  }));

  const fakePlans = pricingCards.map((card, index) => ({
    ...card,
    type: index + 100,
  }));

  useEffect(() => {
    if (plansLoaded && plans.length === 0) {
      dispatch(getPlanPrices());
    }
  }, [plansLoaded, plans?.length]);

  useEffect(() => {
    if (user?.has_voip) {
      setUserCount(user.active_users);
    }
  }, [user]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 600); // adjust the breakpoint as needed
    };

    checkScreenSize(); // initial check
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return (
    <S.PricingSectionWrapper id="pricing-cards-section">
      <S.PricingSectionContainer>
        <S.PricingSectionHeader>
          {heading && <H1 as="h2">{heading}</H1>}
          <UserCount
            value={userCount}
            setValue={setUserCount}
            showInput={countOptions.showInput}
            showSlider={countOptions.showSlider}
            renewalPeriod={renewalPeriod}
            setRenewalPeriod={setRenewalPeriod}
          />
        </S.PricingSectionHeader>
        <S.PricingCardWrapper>
          <S.PricingCardContainer>
            {(plansLoaded ? usedPlans : fakePlans).map((card) => (
              <Fragment key={card.type}>
                {card?.type === 9 && (
                  <S.MostPopularWrapper $isMobile={isMobile}>
                    <Image
                      src={MostPopular.src}
                      alt="Most Popular Label"
                    />
                  </S.MostPopularWrapper>
                )}
                <PricingCard
                  {...card}
                  key={card.type}
                  userCount={userCount}
                  loading={!plansLoaded}
                  renewalPeriod={renewalPeriod}
                />
              </Fragment>
            ))}
          </S.PricingCardContainer>
        </S.PricingCardWrapper>

        <S.FooterWrapper>
          {footer && <P>{footer}</P>}
          {!hideStaticFooter && (
            <P>
              Still Not Sure?{" "}
              <StyledLink href="/voice">Compare our plans.</StyledLink>
            </P>
          )}
        </S.FooterWrapper>
      </S.PricingSectionContainer>
    </S.PricingSectionWrapper>
  );
};

export default PricingSectionComponent;

export const options = [
  {
    description: "",
    displayName: "PricingSectionComponent",
    methods: [],
    props: {
      heading: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
      footer: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
    },
  },
];
